/**
 * Replays an activity the way the connector does.
 *
 * The rules come back normalised into three comparisons and two groupings, so reading them here
 * costs a few lines and keeps the form responsive: a path is recomputed as the user picks answers
 * rather than on a round trip per answer. The reading has to match the connector's, since the same
 * rules decide the path stored on the request and the path the executing service will act on.
 */
const asValues = (value) => {
    if (value === undefined || value === null || value === '') {
        return [];
    }
    return (Array.isArray(value) ? value : [value])
        .filter((item) => item !== undefined && item !== null && item !== '')
        .map((item) => String(item));
};
/** Reads a normalised rule against a set of answers keyed by step key */
export const evaluateDcrFlowRule = (rule, answers) => {
    if (!rule || rule.unreadable) {
        return false;
    }
    if (Array.isArray(rule.all)) {
        return rule.all.every((part) => evaluateDcrFlowRule(part, answers));
    }
    if (Array.isArray(rule.any)) {
        return rule.any.some((part) => evaluateDcrFlowRule(part, answers));
    }
    const values = asValues(answers[String(rule.stepKey)]);
    const wanted = rule.values ?? [];
    switch (rule.op) {
        case 'answered':
            return values.length > 0;
        case 'includes':
            return values.some((value) => wanted.includes(value));
        case 'excludes':
            return !values.some((value) => wanted.includes(value));
        default:
            return false;
    }
};
/** The step keys a rule reads, used to tell whether a screen decides where it leads itself */
const keysRuleReads = (rule) => {
    if (!rule) {
        return [];
    }
    const parts = rule.all ?? rule.any;
    if (Array.isArray(parts)) {
        return parts.flatMap((part) => keysRuleReads(part));
    }
    return rule.stepKey ? [rule.stepKey] : [];
};
/**
 * A screen the replay has to stop at: it has no answer yet and its own answer decides where the
 * activity goes next. The participant could not have moved past it either.
 */
const awaitsAnswer = (screen, answers) => {
    if (!screen.editable || asValues(answers[screen.stepKey]).length > 0) {
        return false;
    }
    return (screen.transition?.conditions ?? []).some((condition) => keysRuleReads(condition.when).includes(screen.stepKey));
};
const destinationFor = (screen, answers) => {
    const { transition } = screen;
    const match = (transition?.conditions ?? []).find((condition) => evaluateDcrFlowRule(condition.when, answers));
    return match ? match.destination : (transition?.default ?? { kind: 'next' });
};
/**
 * The screens the activity visits for a set of answers. A screen the replay does not reach counts
 * as unanswered from there on, which is what the activity does with a screen nobody sees.
 */
export const simulateDcrFlow = (screens, answers) => {
    const indexByStepId = new Map(screens.map((screen, index) => [String(screen.stepId), index]));
    const visited = new Set();
    const path = [];
    let index = 0;
    while (index >= 0 && index < screens.length) {
        const screen = screens[index];
        if (visited.has(String(screen.stepId))) {
            break;
        }
        visited.add(String(screen.stepId));
        path.push(String(screen.stepId));
        if (awaitsAnswer(screen, answers)) {
            break;
        }
        const destination = destinationFor(screen, answers);
        if (destination.kind === 'next') {
            index += 1;
        }
        else if (destination.kind === 'step') {
            index = indexByStepId.get(String(destination.stepId)) ?? -1;
        }
        else {
            index = -1;
        }
    }
    return path;
};
/** The answers on record, keyed the way the rules refer to them */
export const dcrRecordedAnswers = (screens) => screens.reduce((answers, screen) => {
    if (screen.answered && screen.stepKey) {
        answers[screen.stepKey] = screen.value;
    }
    return answers;
}, {});
/**
 * The path for the answers the user has picked. Selections are held per step id, since that is
 * what the form and the stored request are keyed by, and the rules read step keys.
 */
export const dcrFlowPath = (screens, selections) => {
    const answers = screens.reduce((result, screen) => {
        if (Object.prototype.hasOwnProperty.call(selections, String(screen.stepId))) {
            result[screen.stepKey] = selections[String(screen.stepId)];
        }
        else if (screen.answered) {
            result[screen.stepKey] = screen.value;
        }
        return result;
    }, {});
    return simulateDcrFlow(screens, answers);
};
/** The activity's screens as the rest of the form reads them */
export const dcrScreensFromFlow = (flow) => (flow?.screens ?? []).map((screen) => {
    const title = screen.question || screen.name;
    return {
        id: String(screen.stepId),
        stepId: String(screen.stepId),
        stepResponseId: screen.stepResponseId ? String(screen.stepResponseId) : '',
        name: screen.name,
        label: `${screen.order}. ${title}`,
        question: screen.question,
        rawQuestion: screen.rawQuestion,
        variables: flow?.variables,
        type: screen.type,
        order: screen.order,
        // The display label reads as the participant saw it; the raw value is all some screens have
        originalValue: screen.label || screen.value,
        // The recorded choice code. The label above follows the participant's locale, so a review
        // that compared it against the site-locale desired label would flag a translation-only
        // difference as a data change; the code never does.
        originalChoiceValue: screen.value,
        skipped: !screen.answered,
        disabled: screen.disabled,
        editable: screen.editable,
        stepKey: screen.stepKey,
        screen: screen.name,
        hasBranchingLogic: screen.readByRules,
        displayName: `${screen.order}. ${title}`,
        allowMultiples: screen.allowMultiples,
        choices: screen.choices,
    };
});
/**
 * The screens a branching request is described by: every screen holding an answer, whether or not
 * the activity currently reaches it, so a change that strands an answer still accounts for it.
 */
export const dcrBranchingStepsFromFlow = (flow) => (flow?.screens ?? [])
    .filter((screen) => screen.editable)
    .map((screen) => ({
    stepId: String(screen.stepId),
    stepKey: screen.stepKey,
    stepResponseId: screen.stepResponseId ? String(screen.stepResponseId) : '',
    screen: `${screen.order}. ${screen.name}`,
    name: screen.name,
    question: screen.question,
    rawQuestion: screen.rawQuestion,
    variables: flow?.variables,
    originalValue: screen.value,
    originalLabel: screen.label,
    skipped: !screen.answered,
    desiredValue: {
        options: screen.choices.map((choice) => ({ value: String(choice.value), label: choice.label })),
        // A screen the study stopped asking cannot take a new answer, but its old one still counts
        editable: !screen.disabled,
        allowMultiples: screen.allowMultiples,
    },
}));
